import { useState, useEffect, useRef, useCallback } from 'react';
import chatService from '../services/chat/chat.service';
import { IChatUser, IChatConversation, IChatMessage } from '../interfaces/chat/chat.interface';
import { useLoading } from '../components/agent/Context/LoadingContext';
import { apiUrl } from '../variables';

// Flag global para evitar inicialização duplicada
let isInitializing = false;
let initializationPromise: Promise<{ currentUser: IChatUser | null; conversations: IChatConversation[] } | null> | null = null;

export const useChat = () => {
  const { setIsLoading } = useLoading();
  const [currentUser, setCurrentUser] = useState<IChatUser | null>(null);
  const [users, setUsers] = useState<IChatUser[]>([]);
  const [conversations, setConversations] = useState<IChatConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<IChatConversation | null>(null);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pendingMessagesRef = useRef<Map<string, IChatMessage>>(new Map());
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentUserRef = useRef<IChatUser | null>(null);
  const prevCurrentUserRef = useRef<IChatUser | null>(null);
  const canAccessChat = (() => {
    try {
      const dataStudentRaw = localStorage.getItem('dataStudent');
      if (dataStudentRaw) {
        const dataStudent = JSON.parse(dataStudentRaw);
        if (dataStudent?.dataStudent?.hasGraduation) {
          return true;
        }
      }
    } catch (error) {
      // Ignorar erro de parse
    }

    try {
      const alunoStorageRaw = localStorage.getItem('alunoStorage');
      if (alunoStorageRaw) {
        const alunoStorage = JSON.parse(alunoStorageRaw);
        if (alunoStorage?.aluno_graduacao) {
          return true;
        }
      }
    } catch (error) {
      // Ignorar erro de parse
    }

    return false;
  })();

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    const prevUser = prevCurrentUserRef.current;
    const newUser = currentUser;
    prevCurrentUserRef.current = newUser;

    return () => {
      if (prevUser && !newUser) {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      }
    };
  }, [currentUser]);

  useEffect(() => {
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, []);

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    heartbeatIntervalRef.current = setInterval(async () => {
      const user = currentUserRef.current;
      if (user) {
        try {
          await chatService.updateOnlineStatus(true);
        } catch (error) {
          if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
          }
          await chatService.updateOnlineStatus(false).catch(() => {});
          currentUserRef.current = null;
        }
      } else {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      }
    }, 5 * 60 * 1000);
  }, []);

  const initializeChat = useCallback(async (): Promise<{ currentUser: IChatUser | null; conversations: IChatConversation[] } | null> => {
    try {
      if (!canAccessChat) {
        return null;
      }
      setLoading(true);
      const user = await chatService.getCurrentUser();
      if (user) {
        setCurrentUser(user);
        await chatService.updateOnlineStatus(true);
        const convs = await chatService.getConversations();
        setConversations(convs);
        
        startHeartbeat();
        return { currentUser: user, conversations: convs };
      }
      return null;
    } catch (error) {
      const user = currentUserRef.current;
      if (user) {
        await chatService.updateOnlineStatus(false);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [startHeartbeat, canAccessChat]);

  useEffect(() => {
    if (!canAccessChat) {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      currentUserRef.current = null;
      setCurrentUser(null);
      setUsers([]);
      setConversations([]);
      setMessages([]);
      setLoading(false);
      return;
    }

    if (isInitializing && initializationPromise) {
      initializationPromise.then((result) => {
        if (result) {
          setCurrentUser(result.currentUser);
          setConversations(result.conversations);
          setLoading(false);
        }
      }).catch(() => {
        setLoading(false);
      });
      return;
    }

    if (!isInitializing) {
      isInitializing = true;
      initializationPromise = initializeChat();
      initializationPromise.finally(() => {
        isInitializing = false;
        initializationPromise = null;
      });
    }
  }, [initializeChat, canAccessChat]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const user = currentUserRef.current;
      if (user) {
        const token = localStorage.getItem('userStudent');
        if (token && token !== 'null') {
          fetch(`${apiUrl}/chat/user/update-status`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ isOnline: false }),
            keepalive: true,
          });
        }
      }
    };

    const handleTokenExpired = async () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      const user = currentUserRef.current;
      if (user) {
        await new Promise(resolve => setTimeout(resolve, 100));
        await chatService.updateOnlineStatus(false).catch(() => {});
        currentUserRef.current = null;
        setCurrentUser(null);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('token-expired', handleTokenExpired);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('token-expired', handleTokenExpired);
    };
  }, []);

  const loadUsers = async () => {
    if (!canAccessChat) return;

    setIsLoading(true);
    try {
      const allUsers = await chatService.getAllUsers();
      setUsers(allUsers);
    } catch (error) {
      // Erro ao carregar usuários
    } finally {
      setIsLoading(false);
    }
  };

  const loadConversations = async () => {
    if (!canAccessChat) return;

    setIsLoading(true);
    try {
      const convs = await chatService.getConversations();
      setConversations(convs);
      
      setUsers((prevUsers) => {
        const updatedUsers = [...prevUsers];
        
        convs.forEach((conversation) => {
          if (conversation.type === 'direct' && conversation.otherParticipant?.user) {
            const otherUser = conversation.otherParticipant.user;
            const userId = conversation.otherParticipant.userId;
            
            const userIndex = updatedUsers.findIndex((u) => u.id === userId);
            
            if (userIndex !== -1) {
              updatedUsers[userIndex] = {
                ...updatedUsers[userIndex],
                isOnline: otherUser.isOnline ?? updatedUsers[userIndex].isOnline,
              };
            } else if (otherUser.isOnline !== undefined) {
              updatedUsers.push({
                id: userId,
                userId: userId,
                name: otherUser.name,
                avatar: otherUser.avatar ?? null,
                isOnline: otherUser.isOnline,
              });
            }
          }
        });
        
        return updatedUsers;
      });
    } catch (error) {
      // Erro ao carregar conversas
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async (conversationId: number, page: number = 1, append: boolean = false) => {
    if (!currentUser || !canAccessChat) return;

    if (!append) {
      setLoading(true);
    } else {
      setLoadingMoreMessages(true);
    }

    try {
      const { messages: msgs, hasMore } = await chatService.getMessages(conversationId, page, 50);
      
      if (append) {
        setMessages((prev) => [...msgs, ...prev]);
      } else {
        setMessages(msgs);
      }
      
      setHasMoreMessages(hasMore);
      setCurrentPage(page);
      
      const conversation = conversations.find((c) => c.id === conversationId);
      if (conversation && (conversation.unreadCount || 0) > 0) {
        await chatService.markAsRead(conversationId);
      }
    } finally {
      setLoading(false);
      setLoadingMoreMessages(false);
    }
  };

  const loadMoreMessages = async () => {
    if (!selectedConversation || !hasMoreMessages || loadingMoreMessages) return;
    
    const nextPage = currentPage + 1;
    await loadMessages(selectedConversation.id, nextPage, true);
  };

  const selectConversation = async (conversation: IChatConversation) => {
    if (!conversation || !conversation.id) {
      setSelectedConversation(null);
      setMessages([]);
      setHasMoreMessages(false);
      setCurrentPage(1);
      return;
    }
    setSelectedConversation(conversation);
    setCurrentPage(1);
    await loadMessages(conversation.id, 1, false);
  };

  const sendMessage = async (content: string) => {
    if (!selectedConversation || !currentUser || !canAccessChat) return false;

    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const now = new Date().toISOString();
    const timestamp = new Date(now).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    const optimisticMessage: IChatMessage = {
      id: 0,
      conversationId: selectedConversation.id,
      senderId: currentUser.id,
      content: content.trim(),
      messageType: 'text',
      createdAt: now,
      timestamp,
      isOwn: true,
      sender: {
        id: currentUser.id,
        name: currentUser.name,
        avatar: currentUser.avatar,
      },
      status: 'sending',
      tempId,
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    
    pendingMessagesRef.current.set(tempId, optimisticMessage);

    setConversations((prev) => {
      const updated = prev.map((conv) => {
        if (conv.id === selectedConversation.id) {
          return {
            ...conv,
            lastMessage: content.trim(),
            timestamp,
          };
        }
        return conv;
      });
      
      const conversationIndex = updated.findIndex((c) => c.id === selectedConversation.id);
      if (conversationIndex > 0) {
        const [movedConversation] = updated.splice(conversationIndex, 1);
        return [movedConversation, ...updated];
      }
      
      return updated;
    });

    try {
      chatService.sendMessage(selectedConversation.id, content.trim())
        .then((success) => {
          if (!success) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.tempId === tempId ? { ...msg, status: 'error' as const } : msg,
              ),
            );
            pendingMessagesRef.current.delete(tempId);
          }
        })
        .catch(() => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.tempId === tempId ? { ...msg, status: 'error' as const } : msg,
            ),
          );
          pendingMessagesRef.current.delete(tempId);
        });

      return true;
    } catch (error) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.tempId === tempId ? { ...msg, status: 'error' as const } : msg,
        ),
      );
      pendingMessagesRef.current.delete(tempId);
      return false;
    }
  };

  const startConversationWithUser = async (userId: number): Promise<IChatConversation | null> => {
    if (!currentUser || !canAccessChat) return null;

    setIsLoading(true);
    try {
      await loadUsers();
      
      const existingConversationId = await chatService.checkExistingConversation(
        currentUser.id,
        userId,
      );

      if (existingConversationId) {
        const existingConversation = conversations.find((c) => c.id === existingConversationId);
        if (existingConversation) {
          return existingConversation;
        }
        await loadConversations();
        const updatedConversations = await chatService.getConversations();
        return updatedConversations.find((c) => c.id === existingConversationId) || null;
      }

      const newConversationId = await chatService.createDirectConversation(
        currentUser.id,
        userId,
      );

      if (newConversationId) {
        await loadConversations();
        const updatedConversations = await chatService.getConversations();
        return updatedConversations.find((c) => c.id === newConversationId) || null;
      }

      return null;
    } catch (error) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const startGroupConversation = async (
    name: string,
    participantIds: number[],
  ): Promise<IChatConversation | null> => {
    if (!currentUser || !canAccessChat) return null;

    setIsLoading(true);
    try {
      await loadUsers();
      
      const newConversationId = await chatService.createGroupConversation(
        name,
        currentUser.id,
        participantIds,
      );

      if (newConversationId) {
        await loadConversations();
        const updatedConversations = await chatService.getConversations();
        const newConversation = updatedConversations.find((c) => c.id === newConversationId);
        return newConversation || null;
      }

      return null;
    } catch (error) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  return {
    canAccessChat,
    currentUser,
    users,
    setUsers,
    conversations,
    setConversations,
    selectedConversation,
    messages,
    setMessages,
    loading,
    loadingMoreMessages,
    hasMoreMessages,
    loadMoreMessages,
    selectConversation,
    sendMessage,
    loadConversations,
    loadUsers,
    startConversationWithUser,
    startGroupConversation,
    stopHeartbeat,
  };
};

